import React from "react";
import PropTypes from "prop-types";
import { useSelector } from "react-redux";
import { ExternalLink } from "ui/Link";
import isEqual from "lodash/isEqual";
import isObjectLike from "lodash/isObjectLike";
import { Alert, AlertType } from "ui/alert";
import Linkify from "linkify-react";
import moment from "moment";
import "moment-timezone";
import { getUser } from "selectors/commonSelectors";
import {
  getPublishingStatus,
  isRemovableFromQueue,
} from "@/features/post/status";

// Build the detailed failure body from the post's fail reason fields.
const buildFailureBody = (post) => {
  const userDisplayIsObj = isEqual({}, post.userDisplayReason);
  const failReasonIsObj = isEqual({}, post.failReason);
  const userDisplayReason = userDisplayIsObj ? "" : post.userDisplayReason;
  const failReason = failReasonIsObj ? "" : post.failReason;

  if (userDisplayReason) {
    // userDisplayReason can contain links to help articles; wrap them so the
    // user can click through.
    return (
      <Linkify
        options={{
          render: ({ attributes, content }) => {
            const { href, target, rel, ...props } = attributes;
            void target;
            void rel;
            return (
              <ExternalLink href={href} {...props}>
                {content}
              </ExternalLink>
            );
          },
        }}
      >
        {userDisplayReason}
      </Linkify>
    );
  }

  if (failReason) {
    if (isObjectLike(failReason)) {
      return failReason.message
        ? failReason.message
        : "Unable to get failed reason";
    }
    return failReason;
  }

  return "";
};

function PostStatus({ post }) {
  const user = useSelector(getUser);
  const timezone = user?.timezone;
  const timePreference = user?.preferences?.timePreference ?? "24h";
  const dateFormat = timePreference === "12h" ? "D MMM hh:mma" : "D MMM HH:mm";

  const status = getPublishingStatus(post);
  const removable = isRemovableFromQueue(post);

  let title = "";
  let body = "";
  let type = AlertType.warning;
  let defaultExpanded = true;

  switch (status) {
    case "posted":
    case "imported": {
      if (post.isPotentialError) {
        type = AlertType.warning;
        title = "Potential error";
        body = (
          <ExternalLink href="https://help.skedsocial.com/en/articles/3811734-what-is-a-potential-error-for-an-instagram-post">
            This post may not have been posted successfully. Click for more
            info.
          </ExternalLink>
        );
      } else {
        type = AlertType.success;
        title = "Successfully published.";
        body = "Successfully published.";
      }
      break;
    }
    case "posting": {
      type = AlertType.warning;
      title = "Posting now…";
      body = "We are currently posting the post.";
      break;
    }
    case "delayed": {
      type = AlertType.warning;
      title = "Publishing delayed";
      body = removable
        ? "This is taking longer than expected. It hasn't been published yet — you can cancel publishing to stop it going out, then reschedule it for a new time. If the delay persists, please contact our team."
        : "This is taking longer than expected. If the delay persists, please contact our team.";
      break;
    }
    case "retrying": {
      type = AlertType.warning;
      title = "Publishing hit a snag";
      const retryMoment = post.retryAt
        ? timezone
          ? moment(post.retryAt).tz(timezone)
          : moment(post.retryAt)
        : null;
      body =
        retryMoment && retryMoment.isValid()
          ? `Retrying at ${retryMoment.format(dateFormat)}.`
          : "We hit a snag publishing this post and are retrying automatically.";
      break;
    }
    case "queued_publishing": {
      type = AlertType.warning;
      title = "Queued to publish";
      body = (
        <p>
          Waiting for a device to become available for publishing. This is
          because{" "}
          <ExternalLink href="https://help.skedsocial.com/en/articles/3811738-how-does-sked-social-s-instagram-scheduling-work">
            we publish for you using real phones
          </ExternalLink>
          , and at peak times there can sometimes be a small delay. If you
          experience a lengthy delay please contact our team.
        </p>
      );
      break;
    }
    case "manual_publishing": {
      type = AlertType.info;
      title = "Sent for manual publishing";
      body = (
        <p>
          We have sent this post to users for publishing. See the publishing
          information below for more.
        </p>
      );
      defaultExpanded = false;
      break;
    }
    case "canceled": {
      type = AlertType.info;
      title = "Cancelled";
      body = "The post was cancelled before publishing.";
      defaultExpanded = false;
      break;
    }
    case "failed": {
      type = AlertType.error;
      title = "Failed to publish";
      body = buildFailureBody(post);
      break;
    }
    case "potential_error": {
      type = AlertType.warning;
      title = "Potential error";
      body = (
        <ExternalLink href="https://help.skedsocial.com/en/articles/3811734-what-is-a-potential-error-for-an-instagram-post">
          This post may not have been posted successfully. Click for more info.
        </ExternalLink>
      );
      break;
    }
    default:
      // Pre-flight (scheduled) posts have no publishing alert.
      return null;
  }

  if (!body) return null;

  // Successful alerts do not expand and render no body.
  if (type === AlertType.success) {
    return <Alert type={type} icon={type} title={title} isExpandable={false} />;
  }

  return (
    <Alert
      type={type}
      title={title}
      icon={type}
      isExpandable={true}
      defaultExpanded={defaultExpanded}
    >
      {body}
    </Alert>
  );
}

PostStatus.propTypes = {
  post: PropTypes.object,
};

export default PostStatus;
